import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, Table } from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { eventsFetch, isEventsProxyEnabled } from '../events/eventsClient';

/**
 * Public APIs → Subservices: selbstgehostete Dienste (LibreTranslate, Nominatim,
 * Ollama), die perspektivisch auch dediziert verkauft werden. Status + Statistiken
 * aus events-api /v1/subservices (probt die Endpunkte + DB-Stats).
 */
function UpBadge({ ok, label }) {
  return <Badge color={ok ? 'success' : 'danger'} pill>{label ?? (ok ? 'up' : 'down')}</Badge>;
}

function LibreTranslate({ s }) {
  const q = s.stats?.queue || {};
  return (
    <Card className="mb-3"><CardBody>
      <CardTitle tag="h6">{s.name}
        <UpBadge ok={s.up > 0} label={`${s.up ?? 0}/${s.total ?? 0} up`} />
        {s.sellable && <Badge color="info" pill className="ms-1">verkaufbar</Badge>}
      </CardTitle>
      <Row className="g-2 small">
        <Col md={6}>
          <div className="text-muted">Endpunkte (Round-Robin)</div>
          <Table size="sm" borderless className="mb-1">
            <tbody>{(s.endpoints || []).map((e, i) => (
              <tr key={i}><td><code>{e.url}</code></td>
                <td><UpBadge ok={e.ok} /></td>
                <td className="text-muted">{e.latency_ms != null ? `${e.latency_ms} ms` : ''}{e.status ? ` · ${e.status}` : ''}</td></tr>
            ))}</tbody>
          </Table>
          <div className="text-muted">Sprachen: {(s.target_langs || []).map((l) => l.toUpperCase()).join(', ') || '–'}</div>
        </Col>
        <Col md={6}>
          <div className="text-muted">Übersetzungs-Queue</div>
          <div><Badge color="success" pill>{q.done ?? 0}</Badge> fertig ·
            {' '}<Badge color="warning" pill>{(q.pending ?? 0) + (q.running ?? 0)}</Badge> wartend ·
            {' '}<Badge color="danger" pill>{q.failed ?? 0}</Badge> Fehler</div>
          <div className="text-muted mt-1">{s.stats?.translated_events ?? 0} Events übersetzt · {q.total ?? 0} Jobs gesamt</div>
        </Col>
      </Row>
    </CardBody></Card>
  );
}

function Nominatim({ s }) {
  return (
    <Card className="mb-3"><CardBody>
      <CardTitle tag="h6">{s.name}
        <UpBadge ok={s.health?.ok} />
        <Badge color={s.self_hosted ? 'success' : 'secondary'} pill className="ms-1">{s.self_hosted ? 'self-hosted' : 'öffentlich (OSM)'}</Badge>
        {s.sellable && <Badge color="info" pill className="ms-1">verkaufbar</Badge>}
      </CardTitle>
      <div className="small">
        <div className="text-muted"><code>{s.url}</code>{s.health?.latency_ms != null ? ` · ${s.health.latency_ms} ms` : ''}</div>
        <div className="mt-1"><Badge color="success" pill>{s.stats?.geocoded ?? 0}</Badge> verortet ·
          {' '}<Badge color="warning" pill>{s.stats?.pending ?? 0}</Badge> offen ·
          {' '}<Badge color="secondary" pill>{s.stats?.cache ?? 0}</Badge> Cache-Einträge</div>
      </div>
    </CardBody></Card>
  );
}

function Ollama({ s }) {
  return (
    <Card className="mb-3"><CardBody>
      <CardTitle tag="h6">{s.name}
        <UpBadge ok={s.health?.ok} label={s.enabled ? undefined : 'aus'} />
        {s.sellable && <Badge color="info" pill className="ms-1">verkaufbar</Badge>}
      </CardTitle>
      <div className="small">
        <div className="text-muted"><code>{s.url || '–'}</code> · Modell: {s.model || '–'}{s.health?.latency_ms != null ? ` · ${s.health.latency_ms} ms` : ''}</div>
        <div className="mt-1"><Badge color="info" pill>{s.stats?.descriptions_generated ?? 0}</Badge> Beschreibungen generiert</div>
      </div>
    </CardBody></Card>
  );
}

export function SubservicesPage() {
  const q = useQuery({
    queryKey: ['subservices'],
    queryFn: () => eventsFetch('/v1/subservices'),
    refetchInterval: 15_000, retry: false, enabled: isEventsProxyEnabled(),
  });
  const list = q.data?.subservices || [];
  const by = (k) => list.find((s) => s.key === k);

  return (
    <div>
      <PageHeader title="Public APIs · Subservices"
        subtitle="Selbstgehostete Dienste (LibreTranslate, Nominatim, Ollama) — Status + Statistiken. Perspektivisch dediziert verkaufbar." />
      {!isEventsProxyEnabled()
        ? <Alert color="secondary" className="small">Events-Proxy nicht konfiguriert (EVENTS_API_URL).</Alert>
        : q.isLoading ? <Spinner size="sm" />
        : q.isError ? <Alert color="danger" className="small">Laden fehlgeschlagen.</Alert>
        : (
          <>
            {by('libretranslate') && <LibreTranslate s={by('libretranslate')} />}
            {by('nominatim') && <Nominatim s={by('nominatim')} />}
            {by('ollama') && <Ollama s={by('ollama')} />}
            <div className="small text-muted">Stand: {q.data?.ts ? new Date(q.data.ts).toLocaleString() : '—'} · Detail-Metriken (Durchsatz/Latenz-Historie, Pod-Skalierung, Logs) folgen mit dem Gateway/Monitoring-Ausbau.</div>
          </>
        )}
    </div>
  );
}
